import {
  ColumnHeading,
  CustomSortKeyFnMap,
  EntityFilterControlsConfig,
  inactiveRowStyles,
  PaginatedDataTable,
  TableCellStylesFn,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMY } from '@lbcamden/shared-utils';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { GlobalStore } from '~/api/tinybase/GlobalStore';
import { AddNewButton } from '~/client/components/common/Button/AddNewButton';
import { SectionContainer } from '~/client/components/common/SectionContainer';
import {
  useHouseholdMemberList,
  useReferenceListDisplayValue,
  useTenantList,
} from '~/client/hooks/storeHooks';
import { toHouseholdMemberCreate, toPersonPage } from '~/client/utils/navigation';
import { Person } from '~/common/types/people';
import { SortBy } from '~/common/types/search';
import { getPersonNameSortKey } from '~/common/utils/sort';

type Props = {
  propertyId: number;
  /** Cannot be changed after mount */
  isTenants?: boolean;
};

type ColumnId = StringKey<Person>;

const nameSort: SortBy<ColumnId> = { field: 'fullName', direction: 'asc' };
const currentMembersFirstSort: SortBy<ColumnId> = {
  field: 'isHistoric',
  direction: 'asc',
};

const getHeadings = (isTenants?: boolean) =>
  [
    { id: 'fullName', content: 'Name' },
    { id: 'dateOfBirth', content: 'Date of birth' },
    !isTenants && { id: 'relationToTenant', content: 'Relationship to tenant' },
  ].filter(Boolean) as ColumnHeading<ColumnId>[];

const customSortKeyFnMap: CustomSortKeyFnMap<Person, ColumnId> = {
  fullName: getPersonNameSortKey,
};

const filterConfigs: EntityFilterControlsConfig<Person> = [
  [
    {
      type: 'toggle',
      key: 'isHistoric',
      activeValue: false,
      label: 'Current members',
    },
    {
      type: 'toggle',
      key: 'isHistoric',
      label: 'Historic members',
    },
  ],
];

const getMapPersonToRow =
  (refListDisplayValue: GlobalStore['referenceListDisplayValue']) => (person: Person) => ({
    _rowId: String(person.personId),
    fullName: person?.fullName,
    dateOfBirth: formatDMY(person.dateOfBirth),
    relationToTenant: refListDisplayValue('RELATION', person.relationToTenant),
  });

const getCellStyles =
  (people: Person[]): TableCellStylesFn<ColumnId> =>
  (columnId, personId) => {
    const rowPerson = people.find((person) => personId && person.personId === parseInt(personId));
    const { isHistoric } = rowPerson || {};

    return [{ width: columnId === 'fullName' ? 250 : 150 }, isHistoric && inactiveRowStyles];
  };

export const People: FunctionComponent<Props> = ({ propertyId, isTenants }) => {
  const navigate = useNavigate();

  const usePersonList = isTenants ? useTenantList : useHouseholdMemberList;
  const people = usePersonList(propertyId);
  const refListDisplayValue = useReferenceListDisplayValue();

  const headings = useMemo(() => getHeadings(isTenants), [isTenants]);

  const mapPersonToRow = useMemo(
    () => getMapPersonToRow(refListDisplayValue),
    [refListDisplayValue],
  );

  const cellStyles = useMemo(() => getCellStyles(people), [people]);

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(0, 1),
    sm: headings.slice(0, 1),
    md: headings.slice(0, 3),
  });

  const onClickRow = useCallback(
    ({ personId }: Person) => navigate(toPersonPage(propertyId, personId)),
    [],
  );

  const onClickAddNewHouseholdMember = () => navigate(toHouseholdMemberCreate(propertyId));

  return (
    <SectionContainer
      title={isTenants ? 'Current tenants' : 'Household members'}
      headerActions={!isTenants && <AddNewButton onClick={onClickAddNewHouseholdMember} />}
    >
      <PaginatedDataTable
        cellStyles={cellStyles}
        headings={visibleHeadings}
        filterConfigs={!isTenants ? filterConfigs : undefined}
        items={people}
        defaultSort={isTenants ? nameSort : currentMembersFirstSort}
        secondarySort={nameSort}
        customSortKeyFnMap={customSortKeyFnMap}
        mapItemToRow={mapPersonToRow}
        onClickRow={onClickRow}
      />
    </SectionContainer>
  );
};
